import cn from 'classnames';
import Forward16 from '@sats-group/icons/16/forward';
import Text from '@sats-group/fresh-fitness-ui-lib/react/text';
import Price from '@sats-group/fresh-fitness-ui-lib/react/price';
import React from 'react';
import { useId, useState } from 'react';

import type { PaymentSummary as Props } from './payment-summary.types';
import Swoop from '../swoop/swoop';

const PaymentSummary: React.FunctionComponent<Props> = ({
  bindingType,
  club,
  collapseLabel,
  expandLabel,
  membershipInformation,
  membershipLines,
  paymentLines,
  payToday,
  payTodayLabel,
  subscriptionName,
  subscriptionPrice,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const contentId = useId();

  return (
    <aside
      className={cn('payment-summary', {
        'payment-summary--expanded': isExpanded,
      })}
    >
      <button
        aria-controls={contentId}
        aria-expanded={isExpanded}
        aria-label={expandLabel}
        className="payment-summary__collapsed"
        onClick={() => setIsExpanded(true)}
        type="button"
      >
        <Swoop className="payment-summary__swoop" />
        <span className="payment-summary__collapsed-membership">
          <Text elementName="span" theme={Text.themes.emphasis}>
            {subscriptionName} · {club}
          </Text>
          {bindingType ? (
            <Text elementName="span" size={Text.sizes.small}>
              {bindingType}
            </Text>
          ) : null}
        </span>
        <span className="payment-summary__collapsed-total">
          <Text elementName="span" size={Text.sizes.small}>
            {payTodayLabel}
          </Text>
          <Text
            elementName="span"
            size={Text.sizes.header3}
            theme={Text.themes.emphasis}
          >
            {payToday}
          </Text>
        </span>
        <span aria-hidden className="payment-summary__forward">
          <Forward16 />
        </span>
      </button>

      <div className="payment-summary__expanded" id={contentId}>
        <Swoop className="payment-summary__swoop" />
        <button
          aria-label={collapseLabel}
          className="payment-summary__collapse"
          onClick={() => setIsExpanded(false)}
          type="button"
        >
          <span aria-hidden>
            <Forward16 />
          </span>
        </button>

        <div className="payment-summary__membership">
          <div className="payment-summary__plan">
            <Text
              elementName="span"
              size={Text.sizes.header1}
              variant={Text.variants.contrast}
            >
              {subscriptionName}
            </Text>
            <Text elementName="span">{membershipInformation}</Text>
          </div>
          {subscriptionPrice ? (
            <div className="payment-summary__subscription-price">
              <Price {...subscriptionPrice} />
            </div>
          ) : null}
        </div>

        <div className="payment-summary__lines">
          {[...membershipLines, ...paymentLines].map(({ label, value }) => (
            <div className="payment-summary__line" key={label}>
              <Text>{label}</Text>
              <Text
                size={Text.sizes.large}
                theme={Text.themes.emphasis}
                variant={Text.variants.hero}
              >
                {value}
              </Text>
            </div>
          ))}
        </div>

        <div className="payment-summary__total">
          <Text size={Text.sizes.header3}>{payTodayLabel}</Text>
          <Text size={Text.sizes.header3} variant={Text.variants.hero}>
            {payToday}
          </Text>
        </div>
      </div>
    </aside>
  );
};

export default PaymentSummary;
